// useDepHealth — polls /api/v2/health/deps every 5s for the NOC home page
// dependency tiles. Phase 1G leftover #1.
//
// We use react-query rather than the SSE pattern because:
//   - the BFF caches the probe set for 5s anyway, so polling is cheap;
//   - the response is small (<8 KB) and tile rerenders are easy to bound;
//   - admin gating is simpler with a single REST round trip.
import { useQuery } from "@tanstack/react-query";

import { api } from "./api";

export type DepStatus = "healthy" | "degraded" | "down" | "not_configured";

export interface DepResult {
    name: string;
    status: DepStatus;
    latencyMs: number;
    checkedAt: string;
    message?: string;
}

export interface DepHealthResponse {
    deps: DepResult[];
    generatedAt: string;
}

const KEY = ["admin", "depHealth"] as const;

export function useDepHealth(opts: { enabled?: boolean } = {}) {
    return useQuery({
        queryKey: KEY,
        queryFn: () => api.get<DepHealthResponse>("/api/v2/health/deps"),
        refetchInterval: 5_000,
        refetchOnWindowFocus: false,
        staleTime: 4_000,
        enabled: opts.enabled ?? true,
    });
}

/** Map a dep status to a chip CSS modifier. Centralised so DepHealthTile +
 *  any future summary view stay aligned with the same colour semantics. */
export function statusChipClass(status: DepStatus): string {
    switch (status) {
        case "healthy":        return "chip--success";
        case "degraded":       return "chip--warn";
        case "down":           return "chip--danger";
        case "not_configured": return "chip--muted";
    }
}

export function statusLabel(status: DepStatus): string {
    switch (status) {
        case "healthy":        return "Healthy";
        case "degraded":       return "Degraded";
        case "down":           return "Down";
        case "not_configured": return "Not configured";
    }
}
